/**
 * P8 Admin — Onboarding Submissions Inbox.
 * Route: /admin/onboarding/submissions
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Inbox, Sparkles, CheckCircle2, XCircle, MessageSquare, Loader2,
  Building2, User, Globe, Search, Mail, Phone, Briefcase, Tag,
  AlertCircle, FileText,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/use-toast';
import SubmissionDocuments from './SubmissionDocuments';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_FILTERS = [
  { id: '', label: 'All' },
  { id: 'pending_review', label: 'Pending', tone: 'amber' },
  { id: 'info_requested', label: 'Info requested', tone: 'sky' },
  { id: 'approved', label: 'Approved', tone: 'emerald' },
  { id: 'rejected', label: 'Rejected', tone: 'rose' },
];

const ROLE_BADGE = {
  client:   'bg-sky-100 text-sky-800',
  supplier: 'bg-amber-100 text-amber-800',
  partner:  'bg-emerald-100 text-emerald-800',
  investor: 'bg-violet-100 text-violet-800',
  other:    'bg-slate-100 text-slate-700',
};

const PRIORITY_BADGE = {
  strategic: 'bg-rose-600 text-white',
  high:      'bg-orange-500 text-white',
  medium:    'bg-amber-200 text-amber-900',
  low:       'bg-slate-200 text-slate-700',
};

const ENTITY_ICON = { company: Building2, individual: User, organization: Globe };


const OnboardingSubmissions = () => {
  const { toast } = useToast();
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [kpis, setKpis] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [actionDialog, setActionDialog] = useState(null);  // 'reject' | 'info'
  const [actionMessage, setActionMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, s] = await Promise.all([
        axios.get(`${API_URL}/api/onboarding/admin/kpis`, auth),
        axios.get(`${API_URL}/api/onboarding/admin/submissions`, {
          ...auth,
          params: { status: statusFilter || undefined, search: search || undefined, limit: 200 },
        }),
      ]);
      setKpis(k.data); setList(s.data || []);
    } catch (e) {
      toast({ title: 'Failed to load onboarding inbox', description: e?.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [auth, statusFilter, search, toast]);

  useEffect(() => { load(); }, [load]);

  const refreshSelected = useCallback(async (id) => {
    try {
      const r = await axios.get(`${API_URL}/api/onboarding/admin/submissions/${id}`, auth);
      setSelected(r.data);
    } catch { /* swallow */ }
  }, [auth]);

  const approve = useCallback(async (sub) => {
    setActionLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/onboarding/admin/submissions/${sub.id}/approve`, {}, auth);
      toast({ title: 'Approved', description: `CRM entities created for ${r.data.legal_name}.` });
      setSelected(r.data); await load();
    } catch (e) {
      toast({ title: 'Approval failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setActionLoading(false); }
  }, [auth, toast, load]);

  const decide = useCallback(async (sub, action) => {
    setActionLoading(true);
    try {
      const url = action === 'reject'
        ? `${API_URL}/api/onboarding/admin/submissions/${sub.id}/reject`
        : `${API_URL}/api/onboarding/admin/submissions/${sub.id}/request-info`;
      const r = await axios.post(url, { message: actionMessage }, auth);
      toast({ title: action === 'reject' ? 'Rejected' : 'Info requested' });
      setSelected(r.data); setActionDialog(null); setActionMessage('');
      await load();
    } catch (e) {
      toast({ title: 'Action failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setActionLoading(false); }
  }, [auth, toast, actionMessage, load]);

  return (
    <div data-testid="onboarding-inbox">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0F172A] to-[#1E293B] flex items-center justify-center shadow-lg">
          <Inbox className="w-5 h-5 text-[#C9A86A]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42]">Onboarding Inbox</h1>
          <p className="text-sm text-[#6C757D]">Review submitted registrations, approve, reject or request more information.</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="onboarding-kpis">
        <KpiTile label="Total"          value={kpis?.submissions_total ?? '—'} testId="kpi-total" />
        <KpiTile label="Pending"        value={kpis?.submissions_pending ?? '—'} accent="amber"  testId="kpi-pending" />
        <KpiTile label="Info req."      value={kpis?.submissions_info_requested ?? '—'} accent="sky"    testId="kpi-info" />
        <KpiTile label="Approved"       value={kpis?.submissions_approved ?? '—'} accent="emerald"testId="kpi-approved" />
        <KpiTile label="Rejected"       value={kpis?.submissions_rejected ?? '—'} accent="rose"   testId="kpi-rejected" />
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center mb-4" data-testid="onboarding-filters">
        {STATUS_FILTERS.map(f => (
          <button key={f.id || 'all'} onClick={() => setStatusFilter(f.id)} data-testid={`filter-${f.id || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              statusFilter === f.id
                ? 'bg-[#0F172A] text-white border-[#0F172A]'
                : 'bg-white text-[#2B2D42] border-slate-200 hover:border-[#0F172A]'
            }`}>
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 max-w-sm w-full">
          <Search className="w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search legal name, email, ref…"
            className="h-9" data-testid="onboarding-search" />
        </div>
      </div>

      {/* List */}
      <Card><CardContent className="p-0">
        {loading ? (
          <div className="p-8 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Inbox className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            No submissions yet.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="onboarding-submission-list">
            {list.map((s) => {
              const Icon = ENTITY_ICON[s.entity_type] || Building2;
              return (
                <li key={s.id} data-testid={`submission-${s.id}`}
                  onClick={() => { setSelected(s); refreshSelected(s.id); }}
                  className="px-5 py-4 hover:bg-slate-50 cursor-pointer flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#0F172A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-semibold text-[#2B2D42]">{s.legal_name}</span>
                      <span className="text-[10px] font-mono text-[#6C757D]">{s.submission_number}</span>
                      <Badge className={`${ROLE_BADGE[s.role] || ROLE_BADGE.other} text-[10px]`}>{(s.role || '').toUpperCase()}</Badge>
                      {s.ai_priority && (
                        <Badge className={`${PRIORITY_BADGE[s.ai_priority] || ''} text-[10px]`}>
                          <Sparkles className="w-2.5 h-2.5 mr-0.5 inline" /> {s.ai_priority.toUpperCase()}
                        </Badge>
                      )}
                      <StatusPill status={s.status} />
                    </div>
                    <div className="text-xs text-[#6C757D] flex flex-wrap gap-x-3 gap-y-0.5">
                      <span><Mail className="w-3 h-3 inline mr-1" />{s.contact_email}</span>
                      {s.country && <span><Globe className="w-3 h-3 inline mr-1" />{s.country}</span>}
                      {s.ai_pipeline && <span><Briefcase className="w-3 h-3 inline mr-1" />{s.ai_pipeline}</span>}
                      {typeof s.ai_potential_score === 'number' && <span className="text-[#C9A86A] font-semibold">Score {Math.round(s.ai_potential_score)}/100</span>}
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono">{(s.submitted_at || '').slice(0, 10)}</div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent></Card>

      {/* Detail Drawer */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="submission-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selected.legal_name}
                  <span className="text-xs font-mono text-slate-400">{selected.submission_number}</span>
                  <StatusPill status={selected.status} />
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5 text-sm">
                {selected.ai_executive_brief && (
                  <div className="p-4 rounded-lg bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white" data-testid="detail-ai-brief">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#C9A86A] mb-1 flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3" /> AI executive brief
                    </div>
                    <p className="text-white/90 leading-relaxed">{selected.ai_executive_brief}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {(selected.ai_tags || []).map((t, i) => (
                        <Badge key={i} className="bg-[#C9A86A]/15 text-[#E8D6AA] border border-[#C9A86A]/30 text-[10px]">
                          <Tag className="w-2.5 h-2.5 mr-1" />{t}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <DetailGrid>
                  <DetailRow label="Entity type" value={selected.entity_type?.toUpperCase()} />
                  <DetailRow label="Tax ID / TRN" value={selected.tax_id || '—'} />
                  <DetailRow label="Country" value={selected.country || '—'} />
                  <DetailRow label="Website" value={selected.website || '—'} />
                  <DetailRow label="Address" value={selected.address || '—'} full />
                  <DetailRow label="Contact" value={`${selected.contact_name} · ${selected.contact_email}`} />
                  <DetailRow label="Phone" value={selected.contact_phone || '—'} />
                  <DetailRow label="Legal rep." value={selected.legal_representative || '—'} />
                  <DetailRow label="Role" value={(selected.role || '').toUpperCase() + (selected.role_other ? ` — ${selected.role_other}` : '')} />
                  <DetailRow label="AI pipeline" value={selected.ai_pipeline || '—'} />
                  <DetailRow label="AI priority" value={selected.ai_priority?.toUpperCase() || '—'} />
                  <DetailRow label="Potential score" value={typeof selected.ai_potential_score === 'number' ? `${Math.round(selected.ai_potential_score)} / 100` : '—'} />
                </DetailGrid>

                <Section title="Services">
                  <ChipList items={selected.services} testId="detail-services" />
                </Section>
                <Section title="Products">
                  <ChipList items={selected.products} testId="detail-products" />
                </Section>

                {selected.intent && (
                  <Section title="Intent">
                    <div className="p-3 rounded-lg bg-slate-50 text-xs">
                      <div><b>Type:</b> {selected.intent.partnership_type || '—'}</div>
                      <div><b>Objective:</b> {selected.intent.objective || '—'}</div>
                      <div className="mt-1 text-slate-600">{selected.intent.description || '—'}</div>
                    </div>
                  </Section>
                )}

                {(selected.counterparty_id || selected.crm_deal_id) && (
                  <Section title="CRM linkage">
                    <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs space-y-1">
                      {selected.counterparty_id && <div>Counterparty <span className="font-mono">{selected.counterparty_id}</span></div>}
                      {selected.contact_id && <div>Contact <span className="font-mono">{selected.contact_id}</span></div>}
                      {selected.crm_deal_id && <div>Deal <span className="font-mono">{selected.crm_deal_id}</span></div>}
                      {selected.welcome_email_sent && <div className="text-emerald-700"><CheckCircle2 className="w-3 h-3 inline mr-1" />Welcome email sent.</div>}
                    </div>
                  </Section>
                )}
              </div>

              <DialogFooter className="flex flex-wrap gap-2">
                {selected.status === 'approved' && (
                  <Button variant="outline" onClick={() => setDocsOpen(true)}
                    data-testid="action-documents-btn"
                    className="text-[#0F172A]">
                    <FileText className="w-3.5 h-3.5 mr-1" /> KYC Documents
                  </Button>
                )}
                {selected.status === 'pending_review' || selected.status === 'info_requested' ? (
                  <>
                    <Button variant="outline" onClick={() => { setActionDialog('info'); setActionMessage(''); }}
                      data-testid="action-request-info-btn"
                      disabled={actionLoading}>
                      <MessageSquare className="w-3.5 h-3.5 mr-1" /> Request info
                    </Button>
                    <Button variant="destructive" onClick={() => { setActionDialog('reject'); setActionMessage(''); }}
                      data-testid="action-reject-btn"
                      disabled={actionLoading}>
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                    <Button onClick={() => approve(selected)} className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="action-approve-btn"
                      disabled={actionLoading}>
                      {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</>}
                    </Button>
                  </>
                ) : (
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> This submission is already <b>{selected.status}</b>.
                  </div>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Action message dialog */}
      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent data-testid="action-dialog">
          <DialogHeader>
            <DialogTitle>{actionDialog === 'reject' ? 'Reject submission' : 'Request more information'}</DialogTitle>
          </DialogHeader>
          <Textarea value={actionMessage} onChange={(e) => setActionMessage(e.target.value)} rows={5}
            placeholder={actionDialog === 'reject' ? 'Optional reason for rejection (sent to applicant)…' : 'What additional information do you need? (sent to applicant)'}
            data-testid="action-message-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActionDialog(null)}>Cancel</Button>
            <Button
              disabled={actionLoading || (actionDialog === 'info' && !actionMessage.trim())}
              onClick={() => decide(selected, actionDialog)}
              data-testid="action-confirm-btn"
              className={actionDialog === 'reject' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-amber-600 hover:bg-amber-700'}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* P8 Phase C — Documents drawer */}
      <SubmissionDocuments
        open={docsOpen}
        onClose={() => setDocsOpen(false)}
        submission={selected}
        onChanged={() => selected?.id && refreshSelected(selected.id)}
      />
    </div>
  );
};
const KpiTile = ({ label, value, accent, testId }) => {
  const tone = {
    amber: 'border-amber-300 bg-amber-50',
    sky: 'border-sky-300 bg-sky-50',
    emerald: 'border-emerald-300 bg-emerald-50',
    rose: 'border-rose-300 bg-rose-50',
  }[accent] || 'border-slate-200 bg-white';
  return (
    <div className={`p-3 rounded-xl border ${tone}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#6C757D]">{label}</div>
      <div className="text-2xl font-bold text-[#2B2D42] mt-0.5">{value}</div>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const cls = {
    pending_review: 'bg-amber-100 text-amber-800',
    info_requested: 'bg-sky-100 text-sky-800',
    approved: 'bg-emerald-100 text-emerald-800',
    rejected: 'bg-rose-100 text-rose-800',
    draft: 'bg-slate-100 text-slate-700',
  }[status] || 'bg-slate-100 text-slate-700';
  return <Badge className={`${cls} text-[10px] uppercase`}>{(status || '').replace('_', ' ')}</Badge>;
};

const Section = ({ title, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#6C757D] mb-1.5">{title}</div>
    {children}
  </div>
);

const DetailGrid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 p-4 rounded-lg bg-slate-50">{children}</div>
);

const DetailRow = ({ label, value, full }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <div className="text-[10px] uppercase tracking-[0.14em] text-[#6C757D]">{label}</div>
    <div className="text-sm text-[#2B2D42]">{value || '—'}</div>
  </div>
);

const ChipList = ({ items, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {(items || []).length === 0
      ? <span className="text-xs text-slate-400">—</span>
      : items.map((it, i) => (
        <span key={i}
          className={`text-xs px-2.5 py-1 rounded-full border ${
            it.is_new
              ? 'bg-[#C9A86A]/10 border-[#C9A86A]/40 text-amber-900'
              : 'bg-slate-100 border-slate-200 text-slate-700'
          }`}>
          {it.is_new && <Sparkles className="w-2.5 h-2.5 inline mr-1" />}
          {it.ai_normalised_name || it.name}
          {it.ai_category && <span className="ml-1 text-[9px] uppercase text-slate-500">· {it.ai_category}</span>}
        </span>
      ))}
  </div>
);

export default OnboardingSubmissions;
